import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Building2, Receipt, Package, Truck, Loader2, FileText, ShoppingCart, Send, LayoutGrid } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { smartSearch } from "../lib/search";
import { NAV } from "../lib/nav";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";

const SOURCES = [
  { key: "clients", perm: "clients:view", url: "/clients", route: "/clientes", icon: Building2, label: "Clientes", open: true,
    fields: ["business_name", "commercial_name", "nit", "code", "vendor_name"],
    title: (x) => x.business_name, sub: (x) => [x.code, x.nit].filter(Boolean).join(" · "), term: (x) => x.code || x.business_name },
  { key: "invoices", perm: "invoices:view", url: "/invoices", route: "/facturas", icon: Receipt, label: "Facturas", open: true,
    fields: ["number", "client_name", "fel_authorization", "fel_number"],
    title: (x) => x.number, sub: (x) => x.client_name, term: (x) => x.number },
  { key: "quotations", perm: "quotations:view", url: "/quotations", route: "/cotizaciones", icon: FileText, label: "Cotizaciones",
    fields: ["number", "client_name", "vendor_name"],
    title: (x) => x.number, sub: (x) => x.client_name, term: (x) => x.number },
  { key: "orders", perm: "orders:view", url: "/orders", route: "/pedidos", icon: ShoppingCart, label: "Pedidos", open: true,
    fields: ["number", "quotation_number", "client_name"],
    title: (x) => x.number, sub: (x) => x.client_name, term: (x) => x.number },
  { key: "deliveries", perm: "deliveries:view", url: "/deliveries", route: "/envios", icon: Send, label: "Notas de envío", open: true,
    fields: ["number", "order_number", "client_name"],
    title: (x) => x.number, sub: (x) => x.client_name, term: (x) => x.number },
  { key: "products", perm: "inventory:view", url: "/products", route: "/productos", icon: Package, label: "Productos",
    fields: ["code", "description", "brand", "category"],
    title: (x) => x.description, sub: (x) => x.code, term: (x) => x.code || x.description },
  { key: "suppliers", perm: "suppliers:view", url: "/suppliers", route: "/proveedores", icon: Truck, label: "Proveedores",
    fields: ["company", "code", "contact", "country"],
    title: (x) => x.company, sub: (x) => x.code, term: (x) => x.code || x.company },
];

export default function GlobalSearch() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const loaded = useRef(false);
  const sources = useMemo(() => SOURCES.filter((s) => can(s.perm)), [can]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open && !loaded.current) {
      loaded.current = true;
      setLoading(true);
      Promise.all(sources.map((s) => api.get(s.url).then((r) => [s.key, r.data]).catch(() => [s.key, []])))
        .then((pairs) => setData(Object.fromEntries(pairs)))
        .finally(() => setLoading(false));
    }
  }, [open, sources]);

  const groups = useMemo(() => {
    if (!q.trim()) return [];
    return sources
      .map((s) => ({ s, items: smartSearch(data[s.key] || [], q, s.fields).slice(0, 5) }))
      .filter((g) => g.items.length > 0);
  }, [q, data, sources]);

  const modules = useMemo(() => {
    if (!q.trim()) return [];
    const allowed = NAV.filter((n) => can(n.perm));
    return smartSearch(allowed, q, ["label", "group"]).slice(0, 6);
  }, [q, can]);

  const total = groups.reduce((n, g) => n + g.items.length, 0) + modules.length;
  const go = (s, item) => {
    setOpen(false); setQ("");
    const params = new URLSearchParams();
    params.set("q", s.term(item) || "");
    if (s.open) params.set("open", item.id);
    navigate(`${s.route}?${params.toString()}`);
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="global-search-trigger"
        className="hidden xl:flex items-center gap-2 h-9 w-56 xl:w-72 px-3 rounded-xl border border-border/70 bg-muted/40 text-muted-foreground hover:bg-muted transition-colors text-sm">
        <Search className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left truncate">Buscar en todo…</span>
        <kbd className="hidden lg:inline text-[10px] font-mono bg-background border border-border rounded px-1.5 py-0.5">⌘K</kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="p-0 gap-0 max-w-xl" data-testid="global-search-dialog">
          <DialogTitle className="sr-only">Búsqueda global</DialogTitle>
          <DialogDescription className="sr-only">Busca módulos, clientes, facturas, productos y proveedores.</DialogDescription>
          <div className="flex items-center gap-2 border-b border-border px-4 py-3">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="global-search-input"
              placeholder="Buscar módulos, clientes, facturas, productos…"
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground" />
            {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
          <div className="max-h-[60vh] overflow-y-auto p-2">
            {!q.trim() && (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">Escribe para buscar módulos, clientes, facturas, productos y proveedores.</p>
            )}
            {q.trim() && !loading && total === 0 && (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">Sin resultados para “{q}”.</p>
            )}
            {modules.length > 0 && (
              <div className="mb-2">
                <p className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Ir a módulo</p>
                {modules.map((m) => {
                  const Icon = m.icon || LayoutGrid;
                  return (
                    <button key={m.key} onClick={() => { setOpen(false); setQ(""); navigate(m.path); }} data-testid={`gs-module-${m.key}`}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-left">
                      <span className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium truncate">{m.label}</span>
                        <span className="block text-xs text-muted-foreground truncate">{m.group}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {groups.map(({ s, items }) => {              const Icon = s.icon;
              return (
                <div key={s.key} className="mb-2">
                  <p className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{s.label}</p>
                  {items.map((item) => (
                    <button key={item.id} onClick={() => go(s, item)} data-testid={`gs-result-${s.key}-${item.id}`}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-left">
                      <span className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium truncate">{s.title(item) || "—"}</span>
                        {s.sub(item) && <span className="block text-xs text-muted-foreground truncate font-mono">{s.sub(item)}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
